import { useState, useEffect } from 'react'
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import DialogShell from '../common/DialogShell'
import FormSection, { FieldPair } from '../common/FormSection'
import { todayLocalYMD } from '../../utils/dateFormat'
import { jobDateError } from '../../utils/dateRange'
import { isValidJobUrl, ensureHttpProtocol } from '../../utils/validation'
import { ADD_STATUS_OPTIONS, type AddStatusOption } from './addStatusOptions'

/**
 * The client's own add-application form. Lifted out of PortalJobApplications
 * (MWA-297) so the portal's networking page can open the same dialog when a
 * contact refers the client to a job — one form, so the two cannot drift.
 *
 * `referredByName` switches it to the referral case: the job has been heard
 * about rather than applied to, so the date is not prefilled and the heading
 * says where it came from.
 *
 * MWA-367 — outside a referral it carries the staff dialog's Status choice.
 * Without it every client add was stored as Applied, so the client never got
 * the apply-myself / apply-on-my-behalf choice a staff-added job asks for. The
 * default "Opportunity" sends awaitingDecision, the same no-status create a
 * referral uses. A referral offers no choice: it has not been applied to.
 */
export default function PortalAddJobDialog({
  open,
  onClose,
  onAdd,
  referredByName,
}: {
  open: boolean
  onClose: () => void
  onAdd: (data: {
    company: string
    jobTitle: string
    applicationDate?: string
    url?: string
    notes?: string
    location?: string
    status?: string
    awaitingDecision?: boolean
  }) => Promise<void>
  referredByName?: string
}) {
  const isReferral = Boolean(referredByName)
  const emptyForm = () => ({
    company: '',
    jobTitle: '',
    // A referred job has not been applied to, so there is no date to claim.
    applicationDate: isReferral ? '' : todayLocalYMD(),
    url: '',
    notes: '',
    location: '',
    status: '' as AddStatusOption['value'],
  })
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState(emptyForm)

  // The dialog is mounted with its host page, long before anyone chooses to
  // open it as a referral, so the useState initializer above ran while
  // `referredByName` was still undefined and prefilled today's date. Re-apply
  // it on each open, or a referred job records an application that never
  // happened. Verified live on dev before this fix.
  useEffect(() => {
    if (open) {
      setFormData((prev) => ({
        ...prev,
        applicationDate: isReferral ? '' : todayLocalYMD(),
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isReferral])

  // Nothing has been applied to yet, so there is no date to record.
  const isOpportunity = !isReferral && formData.status === ''

  // The applied date is only sent, and only visible, outside an Opportunity.
  const applicationDateError = isOpportunity ? null : jobDateError(formData.applicationDate)

  const handleSubmit = async () => {
    if (!formData.company.trim() || !formData.jobTitle.trim() || applicationDateError) return
    setSubmitting(true)
    try {
      await onAdd({
        ...(isReferral ? {} : isOpportunity ? { awaitingDecision: true } : { status: formData.status }),
        company: formData.company.trim(),
        jobTitle: formData.jobTitle.trim(),
        applicationDate: isOpportunity ? undefined : formData.applicationDate || undefined,
        url: formData.url.trim() ? ensureHttpProtocol(formData.url.trim()) : undefined,
        notes: formData.notes.trim() || undefined,
        location: formData.location.trim() || undefined,
      })
      setFormData(emptyForm())
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      width={760}
      title={isReferral ? `Add a job ${referredByName} told you about` : 'Add Opportunity'}
      footer={
        <>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.company.trim() || !formData.jobTitle.trim() || !isValidJobUrl(formData.url) || !!applicationDateError}
          >
            {submitting ? 'Adding...' : isReferral ? 'Add Job' : 'Add Opportunity'}
          </Button>
        </>
      }
    >
      <FormSection title="The role" first>
        <FieldPair>
          <TextField label="Company" value={formData.company} onChange={(e) => setFormData((p) => ({ ...p, company: e.target.value }))} required fullWidth />
          <TextField label="Job Title" value={formData.jobTitle} onChange={(e) => setFormData((p) => ({ ...p, jobTitle: e.target.value }))} required fullWidth />
        </FieldPair>
      </FormSection>

      {!isReferral && (
        <FormSection title="Where it stands">
          <FormControl fullWidth>
            {/* shrink pairs with displayEmpty, as in AddJobDialog: the default
                option carries the empty value, which a MUI Select would
                otherwise render blank. */}
            <InputLabel shrink>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              displayEmpty
              onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value as AddStatusOption['value'] }))}
            >
              {ADD_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.clientLabel ?? s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormSection>
      )}

      <FormSection title="Dates and place">
        <FieldPair>
          {!isOpportunity && (
            <TextField
              label="Date Applied"
              type="date"
              value={formData.applicationDate}
              onChange={(e) => setFormData((p) => ({ ...p, applicationDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!applicationDateError}
              helperText={applicationDateError ?? (isReferral ? 'Leave blank until you apply.' : '')}
            />
          )}
          <TextField label="Location" value={formData.location} onChange={(e) => setFormData((p) => ({ ...p, location: e.target.value }))} fullWidth />
        </FieldPair>
      </FormSection>

      <FormSection title="Reference">
          <TextField
            label="Job Posting URL"
            value={formData.url}
            onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))}
            fullWidth
            placeholder="https://..."
            error={!isValidJobUrl(formData.url)}
            helperText={!isValidJobUrl(formData.url) ? 'Please enter a valid URL' : ''}
          />
          <TextField label="Notes" value={formData.notes} onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))} multiline rows={3} fullWidth />
      </FormSection>
    </DialogShell>
  )
}
